import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { chevronDownOutline, closeOutline, chevronForwardOutline } from 'ionicons/icons';
import { Subscription, filter } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { PaymentsHttpService } from '../../services/payments/payments-http.service';
import { GuideWidgetStateService } from '../../services/guide-widget-state.service';
import { TourService } from '../../services/tour.service';

interface PaymentStep {
  label: string;
  route: string;
  done: boolean;
  stripeConnectHint?: boolean;
  billingHint?: boolean;
  processingFeesHint?: boolean;
}

/**
 * Only shown when summoned from the help modal's Payments "Start" button —
 * unlike the setup widget there's no persisted "payments done" flag on the
 * org, so this has no auto-show-on-load behavior of its own.
 */
@Component({
  selector: 'app-payments-guide-widget',
  standalone: true,
  imports: [CommonModule, IonIcon],
  templateUrl: './payments-guide-widget.component.html',
  styleUrls: ['./payments-guide-widget.component.scss'],
})
export class PaymentsGuideWidgetComponent implements OnInit, OnDestroy {
  active = false;
  collapsed = false;
  stripeConnected = false;
  hasBillingPlan = false;
  loaded = false;

  /** True while a driver.js hint is showing — hides the widget so it
   *  doesn't sit on top of (and obscure) the element being pointed at. */
  hintActive = false;

  /** Built once per data change, not a getter — see help-modal.component.ts
   *  for why a getter bound to *ngFor freezes the page. Only the two
   *  trackable steps — doneCount/allDone are based on these, not the
   *  quick links below (which never have a "done" state). */
  steps: PaymentStep[] = [];

  /** Static — same two quick links as the help modal's Payments group,
   *  kept in sync with it manually since there's no shared source yet. */
  readonly quickLinks: PaymentStep[] = [
    { label: 'Choose who pays the fees', route: '/settings/payments', done: false, processingFeesHint: true },
    { label: 'View payment history', route: '/payments', done: false },
  ];

  private subs: Subscription[] = [];
  private lastDoneCount = 0;

  constructor(
    private auth: AuthService,
    private paymentsHttp: PaymentsHttpService,
    private router: Router,
    private guideState: GuideWidgetStateService,
    private tourService: TourService,
  ) {
    addIcons({ chevronDownOutline, closeOutline, chevronForwardOutline });
  }

  ngOnInit(): void {
    this.subs.push(
      this.guideState.activeGuide$.subscribe(guide => {
        this.active = guide === 'payments';
        if (this.active) this.load();
      }),
    );

    // Refresh while active so connecting Stripe / adding a billing plan
    // elsewhere is reflected here without a manual reload.
    this.subs.push(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => { if (this.active) this.load(); }),
    );

    this.subs.push(
      this.tourService.active$.subscribe(active => { this.hintActive = active; }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  private load(): void {
    this.loaded = false;
    this.rebuildSteps();
    this.paymentsHttp.getConnectStatus().subscribe({
      next: (res) => { this.stripeConnected = !!res?.connected; this.loaded = true; this.rebuildSteps(); },
      error: () => { this.loaded = true; this.rebuildSteps(); },
    });
    this.paymentsHttp.getOrgBillingPlans().subscribe({
      next: (plans) => { this.hasBillingPlan = Array.isArray(plans) && plans.length > 0; this.rebuildSteps(); },
      error: () => {},
    });
  }

  private rebuildSteps(): void {
    this.steps = [
      { label: 'Connect Stripe account', route: '/settings', done: this.stripeConnected, stripeConnectHint: true },
      { label: 'Add payment for a kid', route: '/kids/list', done: this.hasBillingPlan, billingHint: true },
    ];
    // A step finished while minimized — re-surface the card instead of
    // silently updating behind a collapsed dock.
    const newDoneCount = this.doneCount;
    if (this.collapsed && newDoneCount > this.lastDoneCount) {
      this.collapsed = false;
    }
    this.lastDoneCount = newDoneCount;
  }

  get doneCount(): number {
    return this.steps.filter(s => s.done).length;
  }

  get allDone(): boolean {
    return this.loaded && this.doneCount === this.steps.length;
  }

  get visible(): boolean {
    return this.active && !this.hintActive && !!this.auth.getCurrentUser() && this.auth.canManage();
  }

  onStepClick(step: PaymentStep): void {
    this.guideState.navigateForGuide(this.router, 'payments', step.route, {
      stripeConnectHint: step.stripeConnectHint,
      billingHint: step.billingHint,
      processingFeesHint: step.processingFeesHint,
    });
  }

  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
  }

  dismiss(event: Event): void {
    event.stopPropagation();
    this.guideState.hideGuide('payments');
  }
}
